// resource_type passed through to BE; backend enforces 7-val ACL polymorphism.
// Caller specifies action columns; component does not assume action set.
// Checkbox flips call BE grant/revoke endpoints directly; backend permissions
// are additive rows rather than replace-by-principal sets.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import { AlertCircle, Lock } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ApproverPicker, {
  type ApproverPrincipal,
} from "@/components/shared/pickers/approver-picker";
import {
  useUpsertPermission,
  useDeletePermission,
  type PermissionEntry,
} from "@/hooks/api/mutations/permissions.mutations";
import { useCurrentOrg } from "@/stores";
import {
  resourcePermissionService,
  type ResourcePermissionRow,
  type ResourceType,
} from "@/lib/api/services/resource-permission.service";
import { cn } from "@/utils";
import { formatUserIdentifier } from "@/utils/format-user-name";
import { PermissionMatrixRow, type PermRow } from "./permission-matrix-row";

export interface ActionDef {
  key: string;
  labelKey: string;
  label: string;
}

export interface ResourcePermissionMatrixProps {
  resourceType: string;
  resourceId: string;
  actions: ActionDef[];
  showPrincipalPicker?: boolean;
  compactEmpty?: boolean;
}

// ── Data fetching ─────────────────────────────────────────────────────────────

function useResourcePermissions(resourceType: string, resourceId: string) {
  return useQuery<PermissionEntry[]>({
    queryKey: permissionQueryKey(resourceType, resourceId),
    queryFn: async () => {
      const res = await resourcePermissionService.listByResource(
        resourceType as ResourceType,
        resourceId,
        { limit: 200 }
      );
      return groupPermissionRows(res.data ?? [], resourceType, resourceId);
    },
    enabled: !!resourceType && !!resourceId,
    staleTime: 30_000,
  });
}

function permissionQueryKey(resourceType: string, resourceId: string) {
  return ["permissions", resourceType, resourceId] as const;
}

function principalKey(row: ResourcePermissionRow) {
  const principal = row.principalKind === "role"
    ? `role:${row.principalRole ?? ""}`
    : `${row.principalKind}:${row.principalId ?? ""}`;
  const source = row.source ?? "direct";
  return `${source}:${row.sourceResourceType ?? row.resourceType}:${row.sourceResourceId ?? row.resourceId}:${principal}`;
}

function principalLabel(row: ResourcePermissionRow) {
  if (row.principalKind === "role") return row.principalRole;
  if (row.principalKind === "group") return row.principalGroup?.name;
  if (row.principalKind === "user") {
    const user = row.principalUser;
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    return name || formatUserIdentifier(user, { fallback: "" });
  }
  return undefined;
}

function groupPermissionRows(rows: ResourcePermissionRow[], resourceType: string, resourceId: string): PermissionEntry[] {
  const grouped = new Map<string, PermissionEntry>();
  for (const row of rows) {
    const key = principalKey(row);
    if (!key.endsWith(":")) {
      const existing = grouped.get(key);
      if (existing) {
        if (!existing.actions.includes(row.action)) existing.actions.push(row.action);
      } else {
        grouped.set(key, {
          principalKind: row.principalKind,
          principalId: row.principalId,
          principalRole: row.principalRole,
          principalLabel: principalLabel(row),
          principalUser: row.principalUser,
          principalGroup: row.principalGroup,
          actions: [row.action],
          source: row.source ?? "direct",
          sourceResourceType: row.sourceResourceType ?? row.resourceType ?? resourceType,
          sourceResourceId: row.sourceResourceId ?? row.resourceId ?? resourceId,
          sourceName: row.sourceName ?? undefined,
          editable: row.editable ?? (row.source ?? "direct") === "direct",
        });
      }
    }
  }
  return Array.from(grouped.values());
}

function buildRows(entries: PermissionEntry[], actions: ActionDef[]): PermRow[] {
  return entries.map((e) => ({
    ...e,
    checked: Object.fromEntries(
      actions.map((a) => [a.key, e.actions.includes(a.key)])
    ),
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ResourcePermissionMatrix({
  resourceType,
  resourceId,
  actions,
  showPrincipalPicker = true,
  compactEmpty = false,
}: ResourcePermissionMatrixProps) {
  const t = useTranslations("permissions");
  const org = useCurrentOrg();
  const queryClient = useQueryClient();

  const permissionsQuery = useResourcePermissions(resourceType, resourceId);
  const rawEntriesFromQuery = useMemo(
    () => permissionsQuery.data ?? [],
    [permissionsQuery.data],
  );
  const upsertMutation = useUpsertPermission();
  const deleteMutation = useDeletePermission();

  // Local rows shadow server state — avoids spinner on each checkbox toggle.
  const [rows, setRows] = useState<PermRow[]>([]);
  useEffect(() => {
    setRows(buildRows(rawEntriesFromQuery, actions));
  }, [rawEntriesFromQuery, actions]);

  const pendingCellKeysRef = useRef(new Set<string>());
  const [pendingCellKeys, setPendingCellKeys] = useState<Set<string>>(new Set());

  const rowIdentity = (row: Pick<PermRow, "principalKind" | "principalId" | "principalRole">) =>
    `${row.principalKind}:${row.principalId ?? row.principalRole ?? ""}`;

  const cellKey = (row: Pick<PermRow, "principalKind" | "principalId" | "principalRole">, actionKey: string) =>
    `${rowIdentity(row)}:${actionKey}`;

  const setCellPending = (key: string, pending: boolean) => {
    if (pending) pendingCellKeysRef.current.add(key);
    else pendingCellKeysRef.current.delete(key);
    setPendingCellKeys(new Set(pendingCellKeysRef.current));
  };

  const rollbackToLatestServerRows = () => {
    const cached = queryClient.getQueryData<PermissionEntry[]>(
      permissionQueryKey(resourceType, resourceId)
    );
    setRows(buildRows(cached ?? rawEntriesFromQuery, actions));
  };

  const isRowPending = (row: Pick<PermRow, "principalKind" | "principalId" | "principalRole">) => {
    const prefix = `${rowIdentity(row)}:`;
    for (const key of pendingCellKeys) {
      if (key.startsWith(prefix)) return true;
    }
    return false;
  };

  const handleCheck = (
    rowIdx: number,
    actionKey: string,
    checked: boolean
  ) => {
    const rowBefore = rows[rowIdx];
    const key = rowBefore ? cellKey(rowBefore, actionKey) : "";
    if (!rowBefore || pendingCellKeysRef.current.has(key)) return;

    setRows((prev) => {
      const next = [...prev];
      const actionsNext = checked
        ? Array.from(new Set([...next[rowIdx].actions, actionKey]))
        : next[rowIdx].actions.filter((action) => action !== actionKey);
      const row = {
        ...next[rowIdx],
        actions: actionsNext,
        checked: { ...next[rowIdx].checked, [actionKey]: checked },
      };
      next[rowIdx] = row;
      return next;
    });

    if (rowBefore.editable === false || (rowBefore.source && rowBefore.source !== "direct")) return;

    const payload = {
      resourceType,
      resourceId,
      principalKind: rowBefore.principalKind,
      principalId: rowBefore.principalId,
      principalRole: rowBefore.principalRole,
      actions: [actionKey],
    };
    setCellPending(key, true);
    const mutationOptions = {
      onError: rollbackToLatestServerRows,
      onSettled: () => setCellPending(key, false),
    };
    if (checked) {
      upsertMutation.mutate(payload, mutationOptions);
    } else {
      deleteMutation.mutate(payload, mutationOptions);
    }
  };

  const handleRemove = (row: PermRow) => {
    if (isRowPending(row)) return;
    if (row.actions.length === 0) {
      setRows((prev) => prev.filter((item) => item !== row));
      return;
    }
    deleteMutation.mutate({
      resourceType,
      resourceId,
      principalKind: row.principalKind,
      principalId: row.principalId,
      principalRole: row.principalRole,
      actions: row.actions,
    });
  };

  const handleAddPrincipal = (principal: ApproverPrincipal) => {
    // Optimistic add with no actions — user selects desired permissions via checkboxes.
    const newRow: PermRow = {
      principalKind: principal.principalKind,
      principalId: principal.principalKind === "role" ? undefined : principal.principalId,
      principalRole: principal.principalKind === "role" ? principal.principalId : undefined,
      principalLabel: principal.label,
      actions: [],
      source: "direct",
      sourceName: undefined,
      editable: true,
      checked: Object.fromEntries(actions.map((a) => [a.key, false])),
    };
    setRows((prev) => {
      const exists = prev.find(
        (r) =>
          r.principalKind === principal.principalKind &&
          (r.principalId ?? r.principalRole) === principal.principalId
      );
      return exists ? prev : [...prev, newRow];
    });
  };

  const directRows = rows.filter((r) => r.editable !== false && (!r.source || r.source === "direct"));
  const inheritedRows = rows.filter(
    (r) => r.editable === false || (r.source && r.source !== "direct")
  );

  if (permissionsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="size-5 animate-spin rounded-full border-2 border-[var(--primary)] border-t-transparent" />
      </div>
    );
  }

  if (permissionsQuery.isError) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/30 px-4 py-6 text-center">
        <AlertCircle className="mx-auto mb-2 size-6 text-[var(--muted-foreground)]" aria-hidden />
        <p className="text-[13px] text-[var(--muted-foreground)]">
          {t("share.loadError")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Add principal — Combobox portals above modal per z-index ladder */}
      {showPrincipalPicker && org?.id && (
        <ApproverPicker
          orgId={org.id}
          value={[]}
          onChange={(selected) => {
            const last = selected[selected.length - 1];
            if (last) handleAddPrincipal(last);
          }}
        />
      )}

      {/* Action grid */}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="pb-2 text-left text-[11px] font-semibold text-[var(--muted-foreground)] uppercase tracking-wide w-[40%]">
                  {t("share.addPeople")}
                </th>
                {actions.map((a) => (
                  <th
                    key={a.key}
                    className="pb-2 text-center text-[11px] font-semibold text-[var(--muted-foreground)] uppercase tracking-wide px-2"
                  >
                    {a.label}
                  </th>
                ))}
                <th className="pb-2 w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {directRows.map((row) => (
                <PermissionMatrixRow
                  key={`${row.principalKind}-${row.principalId ?? row.principalRole}`}
                  row={row}
                  rowIdx={rows.indexOf(row)}
                  actions={actions}
                  isInherited={false}
                  onCheck={handleCheck}
                  onRemove={handleRemove}
                  isActionPending={(actionKey) => pendingCellKeys.has(cellKey(row, actionKey))}
                  isRemoveDisabled={isRowPending(row)}
                  t={t}
                />
              ))}
              {inheritedRows.map((row) => (
                <PermissionMatrixRow
                  key={`${row.principalKind}-${row.principalId ?? row.principalRole}-inherited`}
                  row={row}
                  rowIdx={rows.indexOf(row)}
                  actions={actions}
                  isInherited={true}
                  onCheck={handleCheck}
                  onRemove={handleRemove}
                  isActionPending={(actionKey) => pendingCellKeys.has(cellKey(row, actionKey))}
                  isRemoveDisabled={isRowPending(row)}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length === 0 && (
        <div
          className={cn(
            "border border-dashed border-[var(--border)] text-center",
            compactEmpty ? "rounded-lg py-6" : "rounded-xl py-8"
          )}
        >
          <Lock
            className={cn(
              "mx-auto mb-2 text-[var(--muted-foreground)] opacity-40",
              compactEmpty ? "size-6" : "size-7"
            )}
          />
          <p className="text-[13px] text-[var(--muted-foreground)]">
            {t("share.noSharedAccess")}
          </p>
        </div>
      )}
    </div>
  );
}
